import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, Search, Building2, Anchor, Waves } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const SECTORS = [
  {
    code: '35',
    name: 'Côte d\'Émeraude',
    region: 'Ille-et-Vilaine',
    team: 'Équipe Saint-Malo & Côte d\'Émeraude',
    color: '#5493CC',
    icon: Anchor,
    cities: [
      { name: 'Cancale', desc: 'À 2h15 de Paris-Montparnasse en TGV, destination incontournable pour ses huîtres d\'exception.' },
      { name: 'Saint-Malo', desc: 'Cité corsaire intra-muros, l\'une des destinations les plus prisées de Bretagne.' },
      { name: 'Dinard', desc: 'Joyau de la Belle Époque : villas Art Nouveau, promenade du Clair de Lune, plages de l\'Écluse, Saint-Énogat, Prieuré.' },
      { name: 'Saint-Lunaire', desc: 'Station balnéaire familiale et chic : villas Belle Époque, Grande Plage, Longchamp, golf 18 trous mythique.' },
      { name: 'Saint-Coulomb', desc: 'Entre Saint-Malo et Cancale : Pointe du Grouin, plages des Chevrets et de la Guimorais, anses sauvages.' },
      { name: 'Saint-Méloir-des-Ondes', desc: 'Campagne malouine authentique : maraîchage, vergers, fermes en pierre.' },
      { name: 'Dol-de-Bretagne', desc: 'Ancienne capitale de la Bretagne : cathédrale Saint-Samson (XIIᵉ s.), ruelles médiévales, menhir du Champ-Dolent.' },
      { name: 'Saint-Jouan-des-Guérets', desc: 'À 10 minutes de Saint-Malo : porte d\'entrée de la cité corsaire, accès direct à la baie.' },
      { name: 'Pleurtuit', desc: 'À 5 min de Dinard, 15 min de Saint-Malo : campagne bretonne et accès direct à la Côte d\'Émeraude.' },
      { name: 'Saint-Briac-sur-Mer', desc: 'Petite station balnéaire chic et confidentielle entre Dinard et Lancieux, secret bien gardé de la Côte d\'Émeraude.' },
      { name: 'Châteauneuf-d\'Ille-et-Vilaine', desc: 'À 12 min de Saint-Malo et 25 min du Mont-Saint-Michel, commune authentique de l\'arrière-pays malouin.' },
      { name: 'Saint-Benoît-des-Ondes', desc: 'Petit village ostréicole face à la baie du Mont-Saint-Michel, entre Cancale et Saint-Malo.' },
      { name: 'La Richardais', desc: 'Face à Dinard, sur la rive sud de la Rance : vue imprenable sur l\'usine marémotrice et l\'estuaire.' },
      { name: 'Le Minihic-sur-Rance', desc: 'Village authentique de la rive sud de la Rance : cachet de port maritime du XIXᵉ siècle.' },
    ],
    nearby: {
      label: 'Également couvertes dans un rayon de 15 km autour de Saint-Malo',
      list: ['Saint-Suliac', 'La Gouesnière', 'Saint-Père-Marc-en-Poulet', 'Saint-Guinoux', 'Plerguer', 'Hirel', 'Mont-Dol', 'Miniac-Morvan'],
    },
  },
  {
    code: '22',
    name: 'Côtes-d\'Armor',
    region: 'Côte d\'Émeraude',
    team: 'Équipe Saint-Malo & Côte d\'Émeraude',
    color: '#E8945A',
    icon: Waves,
    cities: [
      { name: 'Dinan', desc: 'Cité médiévale parmi les mieux conservées de Bretagne : remparts, maisons à pans de bois, port sur la Rance.' },
      { name: 'Ploubalay', desc: 'Cœur de Beaussais-sur-Mer : point de chute idéal entre Saint-Malo, Dinard et la pointe du Cap Fréhel.' },
      { name: 'Saint-Cast-le-Guildo', desc: 'Élue parmi les plus belles plages d\'Europe : Grande Plage de Saint-Cast, 2 km de sable fin.' },
      { name: 'Lancieux', desc: 'Petite station balnéaire confidentielle : Grande Plage (1,5 km), moulin à vent emblématique, ambiance familiale.' },
      { name: 'Saint-Jacut-de-la-Mer', desc: 'Presqu\'île unique entre baie de l\'Arguenon et baie de Lancieux : 11 plages, île des Hébihens à marée basse.' },
      { name: 'Matignon', desc: 'Bourg historique au cœur de la baie de la Frênaye, qui a donné son nom au célèbre hôtel parisien.' },
      { name: 'Créhen', desc: 'Au bord de l\'estuaire de l\'Arguenon : château du Guildo, ruines romantiques face à la mer.' },
      { name: 'Plancoët', desc: 'Connue pour sa source d\'eau minérale : marché, halles, port fluvial sur l\'Arguenon, sentier de halage vers Saint-Lormel.' },
      { name: 'Saint-Lormel', desc: 'À 3 min de Plancoët, 12 min des plages de Saint-Cast : cadre verdoyant entre vallée de l\'Arguenon et Côte d\'Émeraude.' },
      { name: 'Languenan', desc: 'Bourg patrimonial à 8 min de Plancoët : l\'une des plus anciennes églises romanes de Bretagne (XIIᵉ s., classée MH).' },
      { name: 'Pleudihen-sur-Rance', desc: 'Joyau confidentiel de la vallée de la Rance, entre falaises boisées et eaux saumâtres, à 15 min de Dinan.' },
      { name: 'Corseul', desc: 'Ancienne capitale de la cité gallo-romaine des Coriosolites, l\'un des plus beaux secrets historiques de Bretagne.' },
      { name: 'Pluduno', desc: 'Patrimoine rural exceptionnel : manoirs, longères, fontaines, lavoirs et château de la Bouëxière.' },
    ],
    nearby: {
      label: 'Également couvertes dans un rayon de 20 km autour de Ploubalay (22650)',
      list: ['Pléboulle', 'Bourseul', 'Pléven', 'Saint-Potan', 'Trédias'],
    },
  },
  {
    code: '56',
    name: 'Morbihan',
    region: 'Bretagne sud · Vilaine & Pénerf',
    team: 'Équipe Sud Bretagne (Damgan / La Roche-Bernard)',
    color: '#5493CC',
    icon: Building2,
    cities: [
      { name: 'Damgan', desc: 'Station balnéaire dynamique du Morbihan : 7 km de plages (Kervoyal, Damgan-centre, Pénerf), port ostréicole, GR34.' },
      { name: 'La Roche-Bernard', desc: 'Classée Petite Cité de Caractère et Plus Beaux Détours de France, joyau perché au-dessus de la Vilaine.' },
      { name: 'Ambon', desc: 'Voisine immédiate de Damgan : cadre rural authentique, marais salants, plages familiales (Bétahon, Lanedan, Brouel).' },
      { name: 'Marzan', desc: 'À 5 min de La Roche-Bernard, 20 min de Damgan : pied-à-terre idéal pour le Sud-Morbihan, golfe, Brière, Guérande.' },
      { name: 'Péaule', desc: 'Entre La Roche-Bernard (10 min) et Vannes (25 min) : patrimoine rural, vieux bourg en pierre, crêperies, chapelles.' },
      { name: 'Muzillac', desc: 'Capitale du Pays de Muzillac : office en demeure historique, château de Pen Mur, à 10 min des plages de Damgan.' },
    ],
    nearby: {
      label: 'Également couvertes dans un rayon de 15 km autour de La Roche-Bernard / Damgan',
      list: ['Billiers', 'Nivillac', 'Pénestin', 'Le Guerno', 'Noyal-Muzillac'],
    },
  },
];

const ImplantationsPage = () => {
  const [search, setSearch] = useState('');

  const norm = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const matchesSearch = (text) => norm(text).includes(norm(search));

  const totalCities = SECTORS.reduce(
    (acc, s) => acc + s.cities.length + s.nearby.list.length,
    0
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2D3E50] to-[#3D5266] text-white py-20 md:py-28 px-6 md:px-12">
        <div className="max-w-5xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#E8945A] font-medium mb-4">
            <MapPin className="h-4 w-4" />
            Notre couverture en Bretagne
          </span>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium">
            Nos implantations
          </h1>
          <p className="text-white/70 mt-6 max-w-2xl mx-auto text-lg">
            Nous sommes présents dans les villes opérées par nos partenaires, ainsi que par
            <span className="text-white font-medium"> Terre et Mer Conciergerie</span> — sur la
            Côte d'Émeraude (35 / 22) et dans le Sud-Morbihan (56).
          </p>

          {/* Stats */}
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-2xl mx-auto">
            <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 p-5">
              <div className="font-['Playfair_Display'] text-3xl text-white font-medium">{SECTORS.length}</div>
              <div className="text-[11px] uppercase tracking-wider text-white/60 mt-1">départements</div>
            </div>
            <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 p-5">
              <div className="font-['Playfair_Display'] text-3xl text-[#E8945A] font-medium">{totalCities}</div>
              <div className="text-[11px] uppercase tracking-wider text-white/60 mt-1">communes desservies</div>
            </div>
            <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/15 p-5">
              <div className="font-['Playfair_Display'] text-3xl text-white font-medium">2</div>
              <div className="text-[11px] uppercase tracking-wider text-white/60 mt-1">équipes locales</div>
            </div>
          </div>

          {/* Search */}
          <div className="mt-10 max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Votre commune (ex : Saint-Malo, Dinan, Damgan...)"
              className="pl-12 h-14 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:bg-white/15 rounded-xl"
              data-testid="implantations-search"
            />
          </div>
        </div>
      </section>

      {/* Sectors */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 space-y-20">
        {SECTORS.map((sector) => {
          const Icon = sector.icon;

          const filteredCities = search
            ? sector.cities.filter((c) => matchesSearch(c.name) || matchesSearch(c.desc))
            : sector.cities;
          const filteredNearby = search
            ? sector.nearby.list.filter((c) => matchesSearch(c))
            : sector.nearby.list;

          if (search && filteredCities.length === 0 && filteredNearby.length === 0) {
            return null;
          }

          return (
            <section key={sector.code} className="scroll-mt-24" data-testid={`sector-${sector.code}`}>
              {/* Sector Header */}
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 pb-6 border-b-2 border-gray-100">
                <div className="flex items-start gap-4">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${sector.color}15` }}
                  >
                    <Icon className="h-7 w-7" style={{ color: sector.color }} />
                  </div>
                  <div>
                    <div className="flex items-baseline gap-3">
                      <span
                        className="font-['Playfair_Display'] text-5xl md:text-6xl font-medium"
                        style={{ color: sector.color }}
                      >
                        {sector.code}
                      </span>
                      <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-medium text-[#2D3E50]">
                        {sector.name}
                      </h2>
                    </div>
                    <p className="text-sm text-[#64748B] mt-1">{sector.region}</p>
                  </div>
                </div>
                <div className="md:text-right">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FAFAFA] border border-gray-100">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: sector.color }} />
                    <span className="text-xs uppercase tracking-wider text-[#64748B] font-medium">
                      {sector.team}
                    </span>
                  </span>
                </div>
              </div>

              {/* Cities Grid */}
              {filteredCities.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredCities.map((city) => (
                    <div
                      key={city.name}
                      className="group rounded-xl border border-gray-100 bg-white hover:bg-[#FAFAFA] hover:border-[#E8945A]/30 hover:shadow-md transition-all p-5"
                      data-testid={`city-${city.name}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h3 className="font-['Playfair_Display'] text-lg font-medium text-[#2D3E50] group-hover:text-[#E8945A] transition-colors">
                          {city.name}
                        </h3>
                        <MapPin
                          className="h-4 w-4 flex-shrink-0 mt-1"
                          style={{ color: sector.color }}
                        />
                      </div>
                      <p className="text-sm text-[#64748B] leading-relaxed">{city.desc}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Nearby */}
              {filteredNearby.length > 0 && (
                <div className="mt-8 rounded-2xl bg-[#FAFAFA] border border-gray-100 p-6 md:p-8">
                  <p className="text-sm font-medium text-[#2D3E50] mb-4 flex items-center gap-2">
                    <span className="w-1 h-4 rounded-full" style={{ backgroundColor: sector.color }} />
                    {sector.nearby.label}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {filteredNearby.map((c) => (
                      <span
                        key={c}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-sm text-[#475569]"
                      >
                        <MapPin className="h-3 w-3 text-[#64748B]" />
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </section>
          );
        })}

        {/* No result fallback */}
        {search &&
          SECTORS.every(
            (s) =>
              s.cities.filter((c) => matchesSearch(c.name) || matchesSearch(c.desc)).length === 0 &&
              s.nearby.list.filter((c) => matchesSearch(c)).length === 0
          ) && (
            <div className="text-center py-20">
              <p className="text-[#64748B] text-lg mb-2">
                Votre commune ne semble pas figurer dans nos zones desservies.
              </p>
              <p className="text-[#64748B] text-sm mb-6">
                Contactez-nous, nous étudions chaque demande au cas par cas.
              </p>
              <Button
                asChild
                className="bg-[#E8945A] hover:bg-[#D4844A] rounded-lg"
                data-testid="implantations-contact"
              >
                <a href="mailto:contact@terreetmer-locationdelinge.fr">
                  Nous contacter
                </a>
              </Button>
            </div>
          )}

        {/* CTA bottom */}
        <section className="rounded-3xl bg-gradient-to-br from-[#2D3E50] to-[#3D5266] text-white p-10 md:p-14 text-center mt-12">
          <h3 className="font-['Playfair_Display'] text-3xl md:text-4xl font-medium mb-4">
            Votre logement est dans l'une de ces communes ?
          </h3>
          <p className="text-white/70 mb-8 max-w-2xl mx-auto">
            Commandez votre linge en quelques clics. Livraison directement dans le logement,
            sans engagement.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              asChild
              className="bg-[#E8945A] hover:bg-[#D4844A] rounded-lg h-12 px-8"
              data-testid="implantations-cta-products"
            >
              <Link to="/products">
                Voir nos produits
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="rounded-lg h-12 px-8 border-white/30 text-white hover:bg-white/10 bg-transparent"
            >
              <a
                href="https://terreetmer-conciergerie.fr/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Découvrir Terre et Mer Conciergerie
              </a>
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ImplantationsPage;
